import React, { useCallback, type ComponentProps } from 'react';
import { Tabs } from 'expo-router';
import { FormattedMessage, type MessageDescriptor } from 'react-intl';
import { StyleSheet } from 'react-native';
import type { SharedValue } from 'react-native-reanimated';

import { Navbar } from '@sg/garnish';

import { NAVBAR_TAB_NAMES, TABS } from '../../NavbarV2.constant';
import { navbarV2Messages as messages } from '../../NavbarV2.messages';

// ─────────────────────────────────────────────────────────────────────────────

export const NavbarLink = (props: Props) => {
  const { activeTabIndexSV, isActive, route, onAccountPress, onHomePress, onMenuPress } = props;

  const { name } = route;

  // ─── Handlers ────────────────────────────────────────────────────────

  const onPress = useCallback(() => {
    if (name === TABS.HOME) {
      onHomePress();
    }

    if (name === TABS.MENU) {
      onMenuPress();
    }

    if (name === TABS.ACCOUNT) {
      onAccountPress();
    }
  }, [name, onHomePress, onMenuPress, onAccountPress]);

  // ─────────────────────────────────────────────────────────────────────

  if (!checkIfNavbarTabName(name)) {
    return null;
  }

  const config = NAVBAR_TAB_CONFIGS[name];
  const tabIndex = NAVBAR_TAB_NAMES.indexOf(name);

  return (
    <Navbar.Link
      activeTabIndexSV={activeTabIndexSV}
      iconName={config.iconName}
      iconNameActive={config.iconNameActive}
      isActive={isActive}
      onPress={onPress}
      style={styles.link}
      tabIndex={tabIndex}
    >
      <FormattedMessage {...config.message} />
    </Navbar.Link>
  );
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

function checkIfNavbarTabName(routeName: string): routeName is NavbarTabName {
  return NAVBAR_TAB_NAMES.some((navbarTabName) => navbarTabName === routeName);
}

// ─── Constants ───────────────────────────────────────────────────────────────

const NAVBAR_TAB_CONFIGS = {
  [TABS.HOME]: {
    iconName: 'IconHomeAlternative',
    iconNameActive: 'IconHomeAlternativeFill',
    message: messages.home,
  },
  [TABS.MENU]: {
    iconName: 'IconMenuBowl',
    iconNameActive: 'IconBowlFill',
    message: messages.menu,
  },
  [TABS.ACCOUNT]: {
    iconName: 'IconAccount',
    iconNameActive: 'IconAccountFill',
    message: messages.account,
  },
} satisfies Record<NavbarTabName, NavbarTabConfig>;

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  link: {
    minWidth: 0,
    width: '100%',
  },
});

// ─── Types ──────────────────────────────────────────────────────────────────

type Props = {
  activeTabIndexSV?: SharedValue<number>;
  isActive: boolean;
  onAccountPress: () => void;
  onHomePress: () => void;
  onMenuPress: () => void;
  route: TabBarProps['state']['routes'][number];
};

type TabBarProps = Parameters<NonNullable<ComponentProps<typeof Tabs>['tabBar']>>[0];
type NavbarTabName = (typeof NAVBAR_TAB_NAMES)[number];

type NavbarTabConfig = {
  iconName: ComponentProps<typeof Navbar.Link>['iconName'];
  iconNameActive: ComponentProps<typeof Navbar.Link>['iconNameActive'];
  message: MessageDescriptor;
};
